import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, Filter, Calendar, X } from 'lucide-react'
import Card from './Card'

interface MonthRange {
  startMonth: number
  endMonth: number
}

interface PeriodFilterProps {
  onFilter: (year: number, monthRanges: MonthRange[]) => void
  isLoading?: boolean
  className?: string
  onFilterStart?: () => void
}

const PeriodFilter: React.FC<PeriodFilterProps> = ({
  onFilter,
  isLoading = false,
  className = '',
  onFilterStart
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())
  // Sem seleção do usuário → default para o mês atual (consistente com o backend,
  // que também usa o mês corrente quando nenhum mês é enviado)
  const [selectedMonths, setSelectedMonths] = useState<number[]>([new Date().getMonth() + 1])

  const months = [
    { number: 1, name: 'Jan', short: 'Jan' },
    { number: 2, name: 'Fev', short: 'Fev' },
    { number: 3, name: 'Mar', short: 'Mar' },
    { number: 4, name: 'Abr', short: 'Abr' },
    { number: 5, name: 'Mai', short: 'Mai' },
    { number: 6, name: 'Jun', short: 'Jun' },
    { number: 7, name: 'Jul', short: 'Jul' },
    { number: 8, name: 'Ago', short: 'Ago' },
    { number: 9, name: 'Set', short: 'Set' },
    { number: 10, name: 'Out', short: 'Out' },
    { number: 11, name: 'Nov', short: 'Nov' },
    { number: 12, name: 'Dez', short: 'Dez' }
  ]

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 5 + i)

  const handleMonthClick = (monthNumber: number) => {
    setSelectedMonths(prev => {
      if (prev.includes(monthNumber)) {
        return prev.filter(m => m !== monthNumber)
      } else {
        return [...prev, monthNumber].sort()
      }
    })
  }

  const createMonthRanges = (months: number[]): MonthRange[] => {
    if (months.length === 0) return []
    
    const sortedMonths = [...months].sort()
    const ranges: MonthRange[] = []
    let start = sortedMonths[0]
    let end = sortedMonths[0]

    for (let i = 1; i < sortedMonths.length; i++) {
      if (sortedMonths[i] === end + 1) {
        end = sortedMonths[i]
      } else {
        ranges.push({ startMonth: start, endMonth: end })
        start = sortedMonths[i]
        end = sortedMonths[i]
      }
    }
    ranges.push({ startMonth: start, endMonth: end })
    return ranges
  }

  const handleFilter = () => {
    const ranges = createMonthRanges(selectedMonths)
    
    // Retrai o filtro e inicia o loading
    setIsExpanded(false)
    onFilterStart?.()
    onFilter(selectedYear, ranges)
  }

  const clearSelection = () => {
    setSelectedMonths([])
  }

  const selectAllMonths = () => {
    const allMonths = months.map(month => month.number)
    setSelectedMonths(allMonths)
  }

  const isFilterEnabled = selectedYear > 0 && selectedMonths.length > 0

  return (
    <div className={`w-full ${className}`}>
      <Card className="p-4" hover={false}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              style={{ color: 'var(--text-secondary)' }}
            >
              {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
            </button>
            <Filter className="h-5 w-5" style={{ color: 'var(--primary)' }} />
            <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Filtros de Período
            </h3>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="space-y-6">
                {/* Ano e Meses Selection - Mesma linha */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {/* Ano Selection */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      Selecionar Ano
                    </label>
                    <div className="relative">
                      <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        className="w-full p-3 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{
                          backgroundColor: 'var(--bg-card)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-primary)',
                          minWidth: '120px'
                        }}
                      >
                        {years.map(year => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                      <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 pointer-events-none" style={{ color: 'var(--text-secondary)' }} />
                    </div>
                  </div>

                  {/* Meses Selection */}
                  {selectedYear > 0 && (
                    <div className="lg:col-span-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <label className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Selecionar Meses
                          </label>
                          <button
                            onClick={selectAllMonths}
                            className="flex items-center space-x-1 text-sm text-blue-500 hover:text-blue-600 transition-colors"
                          >
                            <span>Selecionar Todos</span>
                          </button>
                        </div>
                        {selectedMonths.length > 0 && (
                          <button
                            onClick={clearSelection}
                            className="flex items-center space-x-1 text-sm text-red-500 hover:text-red-600 transition-colors"
                          >
                            <X className="h-4 w-4" />
                            <span>Limpar</span>
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {months.map(month => {
                          const isSelected = selectedMonths.includes(month.number)
                          return (
                            <motion.button
                              key={month.number}
                              onClick={() => handleMonthClick(month.number)}
                              className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                                isSelected
                                  ? 'shadow-lg transform scale-105'
                                  : 'hover:scale-105 hover:shadow-md'
                              }`}
                              style={{
                                backgroundColor: isSelected ? '#163965' : 'var(--bg-card)',
                                color: isSelected ? '#ffffff' : 'var(--text-primary)',
                                border: isSelected ? '1px solid #163965' : '1px solid var(--border-primary)'
                              }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              {month.short}
                            </motion.button>
                          )
                        })}
                      </div>
                      {selectedMonths.length > 0 && (
                        <div className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          Meses selecionados: {selectedMonths.map(m => months[m-1].short).join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Filter Button */}
                <div className="flex justify-end">
                  <motion.button
                    onClick={handleFilter}
                    disabled={!isFilterEnabled || isLoading}
                    className={`p-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2 ${
                      isFilterEnabled && !isLoading
                        ? 'shadow-lg transform scale-105'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    style={{
                      backgroundColor: isFilterEnabled && !isLoading ? '#163965' : 'var(--bg-card)',
                      color: isFilterEnabled && !isLoading ? '#ffffff' : 'var(--text-primary)',
                      border: isFilterEnabled && !isLoading ? '1px solid #163965' : '1px solid var(--border-primary)'
                    }}
                    whileHover={isFilterEnabled && !isLoading ? { scale: 1.05 } : {}}
                    whileTap={isFilterEnabled && !isLoading ? { scale: 0.95 } : {}}
                  >
                    <Filter className="h-4 w-4" />
                    <span>{isLoading ? 'Filtrando...' : 'Filtrar'}</span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </div>
  )
}

export default PeriodFilter
